import React from "react";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { MoneyCellRenderer } from "~/pages/Billing/components/MoneyCellRenderer";
import { GRID_COLUMN_FILTER_DEBOUNCE_MS } from "~/pages/Billing/constants";
import { clientNameCell, cellRightAlign, warningIcon } from "./styles_vanilla.css";
import type { GroupedEntityRow } from "./allClientEntitiesDataSource";
import type { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";

const getDisplayName = (data: GroupedEntityRow): string => {
  if (data.type === "client") {
    return data.clientName ?? "";
  } else if (data.type === "billingGroup") {
    return data.billingGroupName ?? "";
  } else {
    return data.entityName ?? "";
  }
};

export const NameCellRenderer = (
  params: ICellRendererParams<GroupedEntityRow>,
): React.ReactNode => {
  const data = params.data;
  if (!data) {
    return null;
  }

  const hasWarning = data.hasWarning;
  const displayName = getDisplayName(data);

  if (hasWarning) {
    return (
      <>
        <Icon
          type="exclamation-triangle"
          color={Colors.Warning600}
          variant="outline"
          size="small"
          data-testid="warning-icon"
          className={warningIcon}
        />
        <span className={clientNameCell}>{displayName}</span>
      </>
    );
  }

  return displayName;
};

export const EumCellRenderer = (
  params: ICellRendererParams<GroupedEntityRow>,
): React.ReactElement | string => {
  // Client rows show "-" since EUM values are at entity level
  if (params.data?.type === "client") {
    return "-";
  }
  // Delegate to MoneyCellRenderer with rounded=true for whole dollar display
  return MoneyCellRenderer({
    value: params.value as number | null | undefined,
    rounded: true,
  });
};

export const EntityNameCellRenderer = (
  params: ICellRendererParams<GroupedEntityRow>,
): React.ReactNode => {
  if (params.data?.type !== "entity") {
    return "-";
  }
  return <NameCellRenderer {...params} />;
};

const containsOnlyFilterParams = {
  filterOptions: ["contains"],
  maxNumConditions: 1,
  debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
};

export const getColumnDefinitions = (): (
  ColDef<GroupedEntityRow> | ColGroupDef<GroupedEntityRow>
)[] => [
  // Hidden rowGroup columns define the hierarchy for AG Grid's Row Grouping mode.
  // - rowGroup: true tells AG Grid to use these fields for grouping
  // - hide: true prevents duplicate columns (AG Grid auto-generates group columns via autoGroupColumnDef)
  // - suppressColumnsToolPanel: true hides them from the sidebar
  // - The values from these fields populate groupKeys when rows are expanded
  {
    field: "clientId",
    headerName: "Client",
    rowGroup: true,
    hide: true,
    suppressColumnsToolPanel: true,
    filter: "agTextColumnFilter",
    filterParams: containsOnlyFilterParams,
  },
  {
    field: "billingGroupId",
    headerName: "Billing group",
    rowGroup: true,
    hide: true,
    suppressColumnsToolPanel: true,
    flex: 1,
  },
  {
    field: "entityName",
    headerName: "Entity",
    flex: 2,
    sortable: true,
    cellRenderer: EntityNameCellRenderer,
  },
  {
    field: "softwareEumUsd",
    headerName: "Software EUM",
    cellRenderer: EumCellRenderer,
    flex: 1,
    sortable: true,
    cellClass: cellRightAlign,
  },
  {
    field: "billableFaEumUsd",
    headerName: "Billable FA EUM",
    cellRenderer: EumCellRenderer,
    flex: 1,
    sortable: true,
    cellClass: cellRightAlign,
  },
  {
    field: "entityId",
    headerName: "Entity ID",
    flex: 1,
    sortable: true,
    cellClass: cellRightAlign,
    valueFormatter: (params) => (typeof params.value === "number" ? String(params.value) : "-"),
  },
  {
    field: "jsqProductLine",
    headerName: "JSQ product line",
    flex: 1,
    sortable: true,
    valueFormatter: (params) => (typeof params.value === "string" ? params.value : "-"),
  },
  {
    field: "billingStatus",
    headerName: "Billing status",
    flex: 1,
    sortable: true,
    valueFormatter: (params) => (typeof params.value === "string" ? params.value : "-"),
  },
];

export const autoGroupColumnDef: ColDef<GroupedEntityRow> = {
  flex: 2,
  sortable: true,
  filter: "agGroupColumnFilter",
  menuTabs: ["generalMenuTab", "filterMenuTab"],
  mainMenuItems: (params) => {
    const defaultItems = params.defaultItems ?? [];
    return defaultItems.filter((item) => item !== "rowUnGroup");
  },
  cellRendererParams: {
    innerRenderer: NameCellRenderer,
    suppressCount: true,
  },
};
